import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/auth-context';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { CompanyAdminView } from '@/components/company/company-admin-view';

export default function CompanyPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, currentMode } = useAuth();
  const { toast } = useToast();
  const [company, setCompany] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [isCompanyMember, setIsCompanyMember] = useState(false);

  useEffect(() => {
    if (!id) {
      navigate('/dashboard');
      return;
    }

    const fetchCompanyData = async () => {
      setLoading(true);
      try {
        // Fetch the company data
        const { data: companyData, error: companyError } = await supabase
          .from('companies')
          .select('*')
          .eq('id', id)
          .single();

        if (companyError) throw companyError;
        setCompany(companyData);

        // Check if user is a member of this company
        if (user) {
          const { data: companyUserData, error: companyUserError } = await supabase
            .from('company_users')
            .select('*')
            .eq('company_id', id)
            .eq('user_id', user.id)
            .eq('is_active', true)
            .maybeSingle();

          if (!companyUserError && companyUserData) {
            setIsCompanyMember(true);
          }
        }
      } catch (error: any) {
        console.error('Error fetching company data:', error);
        toast({
          variant: 'destructive',
          title: 'Error',
          description: error.message || 'Failed to load company data',
        });
        navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    };

    fetchCompanyData();
  }, [id, navigate, user, toast, currentMode]);

  if (loading) {
    return (
      <div className="container py-8 px-4 md:px-6">
        <div className="flex justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
        </div>
      </div>
    );
  }

  if (!company) {
    return (
      <div className="container py-8 px-4 md:px-6">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-2">Company Not Found</h2>
          <p className="text-muted-foreground mb-4">
            The requested company could not be found or may have been deleted.
          </p>
          <Button onClick={() => navigate('/dashboard')}>
            Return to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  // For platform admins, show the admin view
  if (currentMode === 'admin') {
    return <CompanyAdminView companyId={company.id} />;
  }

  // For company members or company admins
  if (isCompanyMember) {
    return <CompanyAdminView companyId={company.id} />;
  }

  // For non-members
  return (
    <div className="container py-8 px-4 md:px-6">
      <Card>
        <CardContent className="p-6">
          <div className="text-center">
            <h2 className="text-2xl font-bold mb-2">{company.name}</h2>
            <p className="text-muted-foreground mb-4">
              You don't have access to this company's admin panel.
            </p>
            <Button onClick={() => navigate('/dashboard')}>
              Return to Dashboard
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}